import { useEffect, useMemo } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { createClient } from '@/api/supabase'
import { USE_MOCK } from '@/config'

export interface TrackPoint {
  id: number
  lat: number
  lon: number
  recorded_at: string
  mode: 'idle' | 'active_order'
  accuracy_m: number | null
  speed_mps: number | null
  order_id: string | null
}

export interface UseInspectorTrackArgs {
  inspectorId: string | undefined
  /** ISO start. Если не задан — последние 24 часа. */
  from?: string
  /** ISO end. Если не задан — `now()`. */
  to?: string
  /** Если задан — фильтруем только точки этого заказа (для секции
   *  «Трек инспектора» на OrderDetailPage). */
  orderId?: string
  /** Smart hybrid: при каждом UPDATE на `profiles` для этого инспектора
   *  дозапросить новые точки. Дефолт: true.
   *
   *  Реалтайм-publication на `inspector_position_history` НЕ используем —
   *  новая точка пишется одной транзакцией с UPDATE profiles, поэтому
   *  существующий `useInspectorsPositionsRealtime` уже даёт нам сигнал.
   *  Это экономит отдельный канал и сотни тысяч событий/день. */
  liveUpdate?: boolean
}

interface CacheKey {
  inspectorId: string
  from: string
  to: string
  orderId?: string
}

function buildKey(a: UseInspectorTrackArgs, defaults: { from: string; to: string }): unknown[] {
  const k: CacheKey = {
    inspectorId: a.inspectorId ?? '',
    from: a.from ?? defaults.from,
    to: a.to ?? defaults.to,
    orderId: a.orderId,
  }
  return ['inspector-track', k]
}

/**
 * Загружает GPS-трек инспектора за период. Smart-hybrid realtime: если
 * `liveUpdate=true`, подписывается на UPDATE по `profiles.id=eq.<inspectorId>`
 * через postgres_changes и инвалидирует свой queryKey при движении инспектора.
 *
 * RLS:
 *   * admin/dispatcher → читают `inspector_position_history` напрямую.
 *   * manager → читают view `inspector_track_for_dealer` (только точки своего
 *     заказа).
 *   * сам инспектор → политика `iph_select_self` на самой таблице.
 *
 * В этой версии всегда читаем напрямую из таблицы; если у пользователя нет
 * доступа — Supabase вернёт пустой массив без ошибки. (View использует
 * OrderDetailPage для менеджерского просмотра — отдельный аргумент `orderId`
 * + соответствующая клиентская логика выбора источника.)
 */
export function useInspectorTrack(args: UseInspectorTrackArgs) {
  const qc = useQueryClient()
  const liveUpdate = args.liveUpdate ?? true

  const defaults = useMemo(() => {
    const now = new Date()
    const dayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000)
    return { from: dayAgo.toISOString(), to: now.toISOString() }
  }, [])

  const queryKey = buildKey(args, defaults)
  const enabled = Boolean(args.inspectorId)

  const query = useQuery<TrackPoint[]>({
    queryKey,
    enabled,
    queryFn: async () => {
      const supabase = createClient()
      const from = args.from ?? defaults.from
      const to = args.to ?? defaults.to
      // PostgREST режет любую выборку до db-max-rows (=1000 в проекте),
      // поэтому тянем страницами через .range() пока страница не окажется
      // короче PAGE_SIZE — это значит дочитали до конца.
      const PAGE_SIZE = 1000
      const MAX_PAGES = 100 // hard-stop: 100k точек, дальше уже UI ляжет
      const all: TrackPoint[] = []
      for (let page = 0; page < MAX_PAGES; page++) {
        let q = supabase
          .from('inspector_position_history')
          .select('id, lat, lon, recorded_at, mode, accuracy_m, speed_mps, order_id')
          .eq('inspector_id', args.inspectorId!)
          .gte('recorded_at', from)
          .lte('recorded_at', to)
          .order('recorded_at', { ascending: true })
          .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1)
        if (args.orderId) q = q.eq('order_id', args.orderId)
        const { data, error } = await q
        if (error) throw error
        const chunk = (data ?? []) as TrackPoint[]
        all.push(...chunk)
        if (chunk.length < PAGE_SIZE) break
      }
      return all
    },
  })

  // Smart-hybrid live update: ловим UPDATE profiles для нашего инспектора.
  // Триггерится на любое изменение profiles этого uid — нас интересует
  // фактически только last_position_at, но профильт on-changes недоступен;
  // ложные срабатывания (изменение, например, full_name) невредны.
  useEffect(() => {
    if (!liveUpdate || !args.inspectorId || USE_MOCK) return
    const supabase = createClient()
    let cancelled = false
    let cleanup: (() => void) | null = null

    void (async () => {
      const { data } = await supabase.auth.getSession()
      if (cancelled) return
      if (data.session) {
        supabase.realtime.setAuth(data.session.access_token)
      }
      const channel = supabase
        .channel(`inspector-track-${args.inspectorId}`)
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'profiles',
            filter: `id=eq.${args.inspectorId}`,
          },
          () => {
            void qc.invalidateQueries({ queryKey })
          },
        )
        .subscribe()
      cleanup = () => {
        void supabase.removeChannel(channel)
      }
    })()

    return () => {
      cancelled = true
      cleanup?.()
    }
    // queryKey — массив, but stable enough since we control its contents.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qc, args.inspectorId, liveUpdate, JSON.stringify(queryKey)])

  return query
}
